import { useQuery } from 'react-query';
import { Addresses } from '../../../api/client/Addresses';
import { Countries } from '../../../api/client/Countries';
import { Currencies } from '../../../api/client/Currencies';
import { Customers } from '../../../api/client/Customers';
import {
  AddressResponse,
  CountryResponse,
  CreateAddressDto,
  CreateCustomerDto,
  CurrencyResponse,
  CustomerResponse,
  RentEquipmentDto,
  RentEquipmentResponse,
  UpdateAddressDto,
  UpdateCustomerDto,
} from '../../../api/client/data-contracts';
import { QueriedData } from '../assets/useAssets';
import { useNotifiedMutation } from '../useNotifiedMutation';
import { defaultApiConfig } from '../../../api';

export interface PaginationData {
  page: number;
  perPage: number;
}
export interface UseCustomersResult {
  customersQuery: QueriedData<CustomerResponse[]>;
  createCustomer: (customer: CreateCustomerDto) => void;
  updateCustomer: (data: { id: string; update: UpdateCustomerDto }) => void;
  deleteCustomer(id: string): void;
  getCustomerById(id: string): CustomerResponse | null;
}

export interface UseAllCustomersResult {
  customersQuery: QueriedData<CustomerResponse[]>;
}

export function useAllCustomers(): UseAllCustomersResult {
  const queryKey = ['customers', 'all'];
  const customersQuery = useQuery(queryKey, () =>
    customersApi.findAllCustomersNoPagination().then((res) => res.data),
  );

  return {
    customersQuery,
  };
}
const customersApi = new Customers(defaultApiConfig);

export interface ReadCustomersQuery {
  search?: string;
  page: number;
  perPage: number;
}
export function useCustomers(
  paginationData?: ReadCustomersQuery,
): UseCustomersResult {
  let query: ReadCustomersQuery = {
    page: 1,
    perPage: 20,
    ...paginationData,
  };

  if (paginationData && paginationData.search) {
    query.search = paginationData.search.toLowerCase();
  }

  const queryKey = ['customers', JSON.stringify(query)];
  const customersQuery = useQuery(queryKey, () =>
    customersApi.findAll(query).then((res) => res.data),
  );
  const { mutate: createCustomer } = useNotifiedMutation(
    (customer: CreateCustomerDto) =>
      customersApi.create(customer).then((res) => res.data),
    {
      successId: 'customer-create-success-message',
      errorId: 'customer-create-fail-message',
      invalidate: queryKey,
    },
  );

  const { mutate: updateCustomer } = useNotifiedMutation(
    (data: { id: string; update: UpdateCustomerDto }) =>
      customersApi.update(data.id, data.update).then((res) => res.data),
    {
      successId: 'customer-update-success-message',
      errorId: 'customer-update-fail-message',
      invalidate: queryKey,
    },
  );

  const { mutate: deleteCustomer } = useNotifiedMutation(
    (id: string) => customersApi.deleteCustomer(id).then((res) => res.data),
    {
      successId: 'customer-delete-success-message',
      errorId: 'customer-delete-fail-message',
      invalidate: queryKey,
    },
  );

  function getCustomerById(id: string) {
    if (!customersQuery.data) {
      return null;
    }

    return customersQuery.data.find((customer) => customer.id === id) || null;
  }
  return {
    customersQuery,
    createCustomer,
    updateCustomer,
    getCustomerById,
    deleteCustomer,
  };
}

export interface UseCustomerAddressesResult {
  addressesQuery: QueriedData<AddressResponse[]>;
  createAddress: (address: Omit<CreateAddressDto, 'customer'>) => void;
  updateAddress: (data: { id: string; update: UpdateAddressDto }) => void;
  getAddressById(id: string): AddressResponse | null;
}

const addressesApi = new Addresses(defaultApiConfig);
export function useCustomerAddresses(
  customerId?: string,
): UseCustomerAddressesResult {
  const queryKey = ['addresses', customerId];
  const query = { customer: customerId };
  if (!query.customer) {
    delete query.customer;
  }
  const addressesQuery = useQuery(queryKey, () =>
    addressesApi
      .findAll(query)
      .then(
        (res: { data: AddressResponse[] | PromiseLike<AddressResponse[]> }) =>
          res.data,
      ),
  );

  const { mutate: createAddress } = useNotifiedMutation(
    (address: Omit<CreateAddressDto, 'customer'>) =>
      addressesApi
        .create({ ...address, customer: customerId || '' })
        .then((res) => res.data),
    {
      successId: 'address-create-success-message',
      errorId: 'address-create-fail-message',
      invalidate: queryKey,
    },
  );

  const { mutate: updateAddress } = useNotifiedMutation(
    (data: { id: string; update: UpdateAddressDto }) =>
      addressesApi.update(data.id, data.update).then((res) => res.data),
    {
      successId: 'address-update-success-message',
      errorId: 'address-update-fail-message',
      invalidate: queryKey,
    },
  );

  function getAddressById(id: string) {
    if (!addressesQuery.data) {
      return null;
    }

    return addressesQuery.data.find((address) => address.id === id) || null;
  }
  return { addressesQuery, createAddress, updateAddress, getAddressById };
}

export interface UseCustomerRentEquipmentsResult {
  rentEquipmentsQuery: QueriedData<RentEquipmentResponse[]>;
  setRentEquipments: (equipments: RentEquipmentDto[]) => void;
  isSaving: boolean;
}

export function useCustomerRentEquipments(
  customerId?: string,
): UseCustomerRentEquipmentsResult {
  const queryKey = ['customer-rent-equipments', customerId];
  const rentEquipmentsQuery = useQuery(
    queryKey,
    () =>
      customersApi.getRentEquipments(customerId || '').then((res) => res.data),
    { enabled: !!customerId },
  );

  const { mutate: setRentEquipments, isLoading: isSaving } =
    useNotifiedMutation(
      (equipments: RentEquipmentDto[]) =>
        customersApi
          .setRentEquipments(customerId || '', { equipments })
          .then((res) => res.data),
      {
        successId: 'customer-rent-equipments-update-success-message',
        errorId: 'customer-rent-equipments-update-fail-message',
        invalidate: queryKey,
      },
    );

  return { rentEquipmentsQuery, setRentEquipments, isSaving };
}

export interface UseCountriesResult {
  countriesQuery: QueriedData<CountryResponse[]>;
  getCountryById(id: string): CountryResponse | null;
}

const countriesApi = new Countries(defaultApiConfig);

export function useCountries(): UseCountriesResult {
  const queryKey = ['countries'];

  const countriesQuery = useQuery(queryKey, () =>
    countriesApi.findAll().then((res) => res.data),
  );

  function getCountryById(id: string): CountryResponse | null {
    if (!countriesQuery.data) {
      return null;
    }

    return countriesQuery.data.find((country) => country.id === id) || null;
  }

  return { countriesQuery, getCountryById };
}

export interface UseCurrenciesResult {
  currenciesQuery: QueriedData<CurrencyResponse[]>;
  getCurrencyById(id: string): CountryResponse | null;
}

const currenciesApi = new Currencies(defaultApiConfig);

export function useCurrencies(): UseCurrenciesResult {
  const queryKey = ['currencies'];
  const currenciesQuery = useQuery(queryKey, () =>
    currenciesApi.findAll().then((res) => res.data),
  );

  function getCurrencyById(id: string) {
    if (!currenciesQuery.data) {
      return null;
    }

    return currenciesQuery.data.find((currency) => currency.id === id) || null;
  }
  return { currenciesQuery, getCurrencyById };
}
